import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { FRAME_META, frameDataUrl, childAge } from "./frameArt";

const W = 1080;
const H = 1350;

const loadImg = (src, cors) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const firstName = (n = "") => n.split(/\s+(BIN|BINTI|BT|B\.|BTE|A\/L|A\/P)\s+/i)[0].trim();

function wrapText(ctx, text, maxW) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  }
  if (line) lines.push(line);
  return lines;
}

async function drawCard(canvas, child, frame) {
  const meta = FRAME_META[frame] || FRAME_META.birthday;
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;
  try { await document.fonts?.ready; } catch (e) { /* abaikan */ }
  // Latar lembut 3 warna
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, meta.bg[0]); g.addColorStop(0.55, meta.bg[1]); g.addColorStop(1, meta.bg[2]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Bintik hiasan
  ctx.globalAlpha = 0.18;
  for (let i = 0; i < 40; i++) {
    ctx.fillStyle = i % 2 ? meta.accent : "#facc15";
    ctx.beginPath();
    ctx.arc((i * 211) % W, (i * 137) % H, 6 + (i % 5) * 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Tajuk ucapan
  ctx.textAlign = "center";
  ctx.fillStyle = meta.text;
  ctx.font = "700 78px Fredoka, Nunito, sans-serif";
  const titleLines = wrapText(ctx, meta.greeting, W - 140);
  titleLines.forEach((l, i) => ctx.fillText(l, W / 2, 140 + i * 86));

  // Gambar + bingkai
  const frameSize = 760;
  const fx = (W - frameSize) / 2;
  const fy = 150 + titleLines.length * 86 - 40;
  const r = (frameSize / 1.5) / 2;
  const cx = W / 2;
  const cy = fy + frameSize / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  let drawn = false;
  if (child.photo_url) {
    try {
      const isHttp = /^https?:/i.test(child.photo_url);
      const img = await loadImg(child.photo_url, isHttp);
      const s = Math.max((r * 2) / img.width, (r * 2) / img.height);
      ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
      drawn = true;
    } catch (e) { drawn = false; }
  }
  if (!drawn) {
    const pg = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
    pg.addColorStop(0, meta.accent); pg.addColorStop(1, "#fde68a");
    ctx.fillStyle = pg;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.fillStyle = "#fff";
    ctx.font = "700 220px Fredoka, Nunito, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText((child.name || "?").charAt(0).toUpperCase(), cx, cy + 10);
    ctx.textBaseline = "alphabetic";
  }
  ctx.restore();
  const frameImg = await loadImg(frameDataUrl(frame));
  ctx.drawImage(frameImg, fx, fy, frameSize, frameSize);

  // Nama & umur
  const ny = fy + frameSize + 40;
  ctx.fillStyle = "#1f2937";
  ctx.font = "800 64px Nunito, Fredoka, sans-serif";
  ctx.fillText(firstName(child.name || ""), W / 2, ny);
  ctx.fillStyle = meta.accent;
  ctx.font = "700 38px Nunito, sans-serif";
  const age = childAge(child.dob);
  const sub = frame === "birthday" && age !== null && age > 0 ? `Kini ${age} tahun!` : (child.class_name || "").replace(/\s*\(.*\)\s*/, "");
  if (sub) ctx.fillText(sub, W / 2, ny + 58);
  // Kaki
  ctx.fillStyle = "#64748b";
  ctx.font = "600 30px Nunito, sans-serif";
  ctx.fillText("Dengan kasih sayang, Destiny Junior", W / 2, H - 50);
}

export default function ShareFrameDialog({ open, onOpenChange, child, frames = [] }) {
  const canvasRef = useRef(null);
  const [frame, setFrame] = useState(frames[0]);
  const [busy, setBusy] = useState(true);
  const [preview, setPreview] = useState("");

  useEffect(() => { if (open) setFrame(frames[0]); }, [open, frames]);

  const render = useCallback(async () => {
    if (!child || !frame || !canvasRef.current) return;
    setBusy(true);
    try {
      await drawCard(canvasRef.current, child, frame);
      setPreview(canvasRef.current.toDataURL("image/jpeg", 0.9));
    } catch (e) {
      toast.error("Gagal menyediakan gambar");
    } finally {
      setBusy(false);
    }
  }, [child, frame]);

  useEffect(() => { if (open) { const t = setTimeout(render, 50); return () => clearTimeout(t); } }, [open, render]);

  const fileName = `${firstName(child?.name || "anak").replace(/\s+/g, "_")}_${frame}.jpg`;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/jpeg", 0.92));

  const download = async () => {
    const blob = await getBlob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success("Gambar dimuat turun");
  };

  const share = async () => {
    try {
      const blob = await getBlob();
      const file = new File([blob], fileName, { type: "image/jpeg" });
      const meta = FRAME_META[frame];
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: meta.greeting, text: `${meta.greeting} dari ${firstName(child.name)}! - Destiny Junior` });
      } else {
        await download();
        toast.info("Peranti ini tidak menyokong kongsi terus. Gambar telah dimuat turun - kongsi melalui WhatsApp/Instagram.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Gagal berkongsi");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4 sm:p-5 max-h-[94vh] overflow-y-auto" data-testid="share-frame-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-pink-500" /> Kongsi Gambar Berbingkai</DialogTitle>
          <DialogDescription>Pilih bingkai, kemudian kongsi ke WhatsApp, Instagram atau keluarga.</DialogDescription>
        </DialogHeader>
        {frames.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="share-frame-options">
            {frames.map((f) => (
              <button key={f} onClick={() => setFrame(f)} data-testid={`share-frame-opt-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${frame === f ? "bg-pink-500 text-white border-pink-500" : "bg-white text-slate-600 border-slate-200 hover:border-pink-300"}`}>
                {FRAME_META[f]?.label}
              </button>
            ))}
          </div>
        )}
        <div className="relative rounded-2xl overflow-hidden border border-slate-100 bg-slate-50 aspect-[4/5] mx-auto w-full" style={{ maxWidth: "min(100%, calc(56vh * 0.8))" }}>
          {preview && <img src={preview} alt="Pratonton" className="w-full h-full object-contain" data-testid="share-frame-preview" />}
          {busy && <div className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>}
        </div>
        <canvas ref={canvasRef} className="hidden" />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={download} disabled={busy} data-testid="share-frame-download"><Download className="w-4 h-4 mr-2" /> Muat Turun</Button>
          <Button onClick={share} disabled={busy} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="share-frame-share"><Share2 className="w-4 h-4 mr-2" /> Kongsi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
